import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import {
  useAssociateMultipleIncidentsToSingleCaseMutation,
  useCreateCaseMutation,
  useFacilityNamesQuery,
} from '@/graphql/generated/hooks'
import { getFacilityOptions } from '@/utils/forms/optionBuilders'
import { MIN_CHAR_COUNT, SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

import { CreateCaseFormInputsIProps } from '../types/types'

interface UseCreateCaseIProps {
  incidentId?: string

  onClose: () => void
}

export const useCreateCase = ({ incidentId, onClose }: UseCreateCaseIProps) => {
  const [createCase] = useCreateCaseMutation()
  const [associateIncidentsToCase] =
    useAssociateMultipleIncidentsToSingleCaseMutation()
  const [isLoading, setIsLoading] = useState(false)
  const [facilityName, setFacilityName] = useState('')
  const [debouncedfacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )
  const { showSuccess, showError } = useToasts()

  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityNamesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter:
          debouncedfacilityName?.length >= MIN_CHAR_COUNT
            ? { nameLike: debouncedfacilityName }
            : undefined,
      },
    })

  const facilityOptions = getFacilityOptions(facilitiesData)

  const onCreateCase: SubmitHandler<CreateCaseFormInputsIProps> = async (
    values
  ) => {
    const facilityIds = values.facilities?.map((f) => f.value) || []
    setIsLoading(true)
    try {
      const { data } = await createCase({
        variables: {
          input: {
            description: values?.description,
            facilityIds,
            name: values?.name,
          },
        },
        refetchQueries: ['Cases'],
      })
      const caseReturnedName = data.createCase?.case?.name
      const caseId = data.createCase?.case?.id
      // If incidentId, associate it with the case
      if (incidentId) {
        await associateIncidentsToCase({
          variables: { input: { caseId, incidentIds: [incidentId] } },
        })
      }

      showSuccess(`Successfully created ${caseReturnedName}`)
      setIsLoading(false)
      onClose()
    } catch (error) {
      showError(error.message)
      setIsLoading(false)
    }
  }

  return {
    facilitiesData,
    facilityOptions,
    isFacilitiesLoading,
    isLoading,
    onCreateCase,
    setFacilityName,
  }
}
